import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ServiceLayout, { ContentSection } from "@/components/site/ServiceLayout";
import { Reveal, TickList } from "@/components/site/Reveal";

const HELP_WITH = [
  "Trauma and PTSD",
  "Anxiety and depression",
  "Grief and loss",
  "Attachment and relationship difficulties",
  "Emotional regulation",
  "Workplace stress and burnout",
  "Neurodivergence",
  "Adjustment to illness, injury or disability",
  "Childhood and developmental trauma",
];

const APPOINTMENTS = [
  "In person at our Ashmore rooms",
  "In-home visits across the Gold Coast, Logan and Brisbane",
  "Telehealth appointments where appropriate",
];

export default function IndividualTherapy() {
  return (
    <ServiceLayout
      eyebrow="Services"
      title="Individual Therapy"
      lead="One-to-one, trauma-informed therapy for adults, adolescents and children — paced to you, and grounded in evidence-informed practice."
      image="https://images.unsplash.com/photo-1595104615356-cbe9c4364513?crop=entropy&cs=srgb&fm=jpg&q=85"
      imageAlt="Quiet forest path in soft light"
    >
      <ContentSection eyebrow="Who this helps" title="We may be able to help with">
        <TickList items={HELP_WITH} />
      </ContentSection>

      <ContentSection eyebrow="Our approach" title="Therapy shaped around you" pale>
        <Reveal>
          <p className="max-w-3xl text-base sm:text-lg leading-relaxed text-mutedsoft">
            Therapy begins with understanding what has happened, what is happening now and what you would like to
            change. Sessions move at your pace, with clear goals we agree on together.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <Accordion type="single" collapsible className="mt-8 max-w-3xl" data-testid="approaches-accordion">
            <AccordionItem value="approaches" className="rounded-2xl border border-euca-line bg-euca-mist/50 px-6">
              <AccordionTrigger className="text-left font-serif text-xl font-medium text-euca-dark hover:no-underline" data-testid="approaches-toggle">
                Treatment approaches we may draw from
              </AccordionTrigger>
              <AccordionContent className="text-base leading-relaxed text-mutedsoft">
                Depending on your needs, therapy may draw from EMDR, ACT, DBT-informed interventions, CBT,
                attachment-based approaches, interpersonal psychotherapy and trauma-informed psychotherapy. The
                approach is always explained and agreed with you before we begin.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="children" className="mt-4 rounded-2xl border border-euca-line bg-euca-mist/50 px-6">
              <AccordionTrigger className="text-left font-serif text-xl font-medium text-euca-dark hover:no-underline" data-testid="children-approaches-toggle">
                Approaches for children and young people
              </AccordionTrigger>
              <AccordionContent className="text-base leading-relaxed text-mutedsoft">
                For children and young people, sessions may include play therapy and video-game-informed therapy —
                developmentally appropriate ways of helping younger people express, process and make sense of their
                experiences.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </Reveal>
      </ContentSection>

      <ContentSection eyebrow="Appointments" title="Appointment options">
        <TickList items={APPOINTMENTS} columns={1} />
        <Reveal delay={0.15}>
          <p className="mt-10 max-w-3xl text-base leading-relaxed text-mutedsoft">
            <span className="font-semibold text-charcoal">Fees and funding.</span> A Medicare rebate of $89.50 per
            session is available with an eligible referral, and we accept clients through NSW Victims Assist and
            Open Arms. Private health rebates may also apply. See{" "}
            <Link to="/fees-funding" className="font-semibold text-euca hover:underline" data-testid="individual-fees-link">
              Fees &amp; Funding
            </Link>{" "}
            for details, or contact the practice to confirm current fees.
          </p>
        </Reveal>
      </ContentSection>
    </ServiceLayout>
  );
}
